// @ts-nocheck
/**
 * Renames a team's PERMANENT record (name / age group / gender), shared across
 * every event. Three fields only: display_name is kept equal to the team name
 * server-side, and age group is always a dropdown — free text is how "17U",
 * "17u - Boys", "14& under" and "High school" all became separate ages.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { getRlTeams, updateRlTeam } from "@/lib/rlTeams";
import { cleanTeamName } from "@/lib/teamNameClean";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const GENDERS = ["boys", "girls", "coed"];

/**
 * RecruitLook owns team records, so both the read and the write go there.
 * Nothing about a team is stored or corrected locally.
 */
async function loadTeam(teamId: string) {
  const map = await getRlTeams([teamId], { force: true });
  const team = map[teamId];
  if (!team) throw new Error("RecruitLook doesn't know this team.");
  return team;
}

export default function TeamRenameDialog({
  teamId,
  open,
  onOpenChange,
  eventId = null,
  onSaved,
}: {
  teamId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId?: string | null;
  onSaved?: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [team, setTeam] = useState<any>(null);
  const [impact, setImpact] = useState<any>(null);
  const [collisions, setCollisions] = useState<any[]>([]);
  const [ageOptions, setAgeOptions] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !teamId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setCollisions([]);
      try {
        const t = await loadTeam(teamId);
        if (cancelled) return;
        setTeam(t);
        setImpact(null);
        setName(t.canonical_name || t.display_name || t.name || "");
        setAge(t.age_group || "");
        setGender((t.gender || "").toLowerCase());
      } catch (e: any) {
        if (cancelled) return;
        setError(
          e?.message ||
            "We couldn't reach RecruitLook to load this team. Try again in a moment."
        );
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, teamId]);

  // Age groups come from the event's configured divisions. Without an event
  // (sidebar Teams list) fall back to the ages already in use, so the control
  // is still a dropdown rather than a free-text box.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const q = eventId
        ? (supabase as any).from("event_age_groups").select("name").eq("event_id", eventId)
        : (supabase as any).from("event_age_groups").select("name").limit(500);
      const { data } = await q;
      if (cancelled) return;
      const names = Array.from(
        new Set((data ?? []).map((r: any) => String(r.name || "").trim()).filter(Boolean)),
      ).sort((a: string, b: string) => a.localeCompare(b, undefined, { numeric: true }));
      setAgeOptions(names as string[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, eventId]);

  // The team's current age may predate the event's list — keep it selectable so
  // opening the dialog never silently blanks it.
  const ages = useMemo(() => {
    const all = [...ageOptions];
    if (age && !all.some((a) => a.toLowerCase() === age.toLowerCase())) all.unshift(age);
    return all;
  }, [ageOptions, age]);

  const save = useCallback(async () => {
    if (!teamId || !name.trim()) return;
    setSaving(true);
    try {
      // A typed name may still carry an age or a gender word — strip it and let
      // it fill the fields it belongs in.
      const cleaned = cleanTeamName(name);
      const finalName = cleaned.name || name.trim();
      const finalGender = gender || cleaned.gender || "";
      const finalAge = age || cleaned.ageGroup || "";
      setName(finalName);
      setGender(finalGender);
      setAge(finalAge);
      try {
        await updateRlTeam(teamId, {
          name: finalName,
          age_group: finalAge,
          gender: finalGender,
        });
      } catch (e: any) {
        // RecruitLook is the authority — show whatever it says, plainly.
        toast.error(
          e?.message || "RecruitLook couldn't save that change. Try again."
        );
        return;
      }
      toast.success("Team record updated on RecruitLook");
      onOpenChange(false);
      onSaved?.();
    } finally {
      setSaving(false);
    }
  }, [teamId, name, age, gender, eventId, onOpenChange, onSaved]);

  const collisionMessage =
    collisions.length > 0
      ? `A team called ${name.trim()} already exists at ${[age, gender].filter(Boolean).join(" ")}.`
      : null;

  // A team with no history doesn't need a warning about its history.
  const showImpact = impact && (impact.past_events > 0 || impact.rosters > 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onOpenChange(false)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit team</DialogTitle>
          <DialogDescription>
            This is the team's permanent record, shared across every event.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <p className="py-6 text-sm text-red-600">{error}</p>
        ) : (
          <div className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="rename-name">Team name</Label>
              <Input
                id="rename-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Bacon Red"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Age group</Label>
                <Select value={age} onValueChange={setAge}>
                  <SelectTrigger id="rename-age">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {ages.map((a) => (
                      <SelectItem key={a} value={a}>
                        {a}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Gender</Label>
                <Select value={gender} onValueChange={setGender}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {GENDERS.map((g) => (
                      <SelectItem key={g} value={g}>
                        {g[0].toUpperCase() + g.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {showImpact && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <div className="flex gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>
                    This renames the team everywhere, including {impact.past_events} past event
                    {impact.past_events === 1 ? "" : "s"} and {impact.rosters} roster
                    {impact.rosters === 1 ? "" : "s"}.
                  </p>
                </div>
              </div>
            )}

            {collisionMessage && (
              <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {collisionMessage}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving || loading || !name.trim() || collisions.length > 0}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
